import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  Avatar,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import LockResetIcon from '@mui/icons-material/LockReset';
import PeopleIcon from '@mui/icons-material/People';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useAuth } from '@/hooks/useAuth';
import { appConfig } from '@/config/app-config';
import ChangePasswordDialog from '@/components/auth/ChangePasswordDialog';
import chatTAKUMILogo from '@/assets/icon_chatTAKUMI.svg';

// 管理者グループ名
const ADMIN_GROUP_NAME = 'administrators';

interface AppLayoutProps {
  children: React.ReactNode;
  onNavigateToUserManagement?: () => void;
  onNavigateToTop?: () => void;
}

/**
 * アプリケーション全体のレイアウト
 */
const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  onNavigateToUserManagement,
  onNavigateToTop,
}) => {
  const { userInfo, loading, handleSignOut } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const open = Boolean(anchorEl);

  // 管理者かどうか
  const isAdmin = userInfo?.groups.includes(ADMIN_GROUP_NAME);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleUserManagement = () => {
    handleClose();
    onNavigateToUserManagement?.();
  };

  const handleLogout = () => {
    handleClose();
    handleSignOut();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* ヘッダー */}
      <AppBar position="static" color="primary" elevation={1}>
        <Toolbar>
          <Box
            onClick={onNavigateToTop}
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: onNavigateToTop ? 'pointer' : 'default',
              '&:hover': onNavigateToTop ? { opacity: 0.8 } : {},
            }}
          >
            <img src={chatTAKUMILogo} alt="ChatTAKUMI" style={{ height: '28px', width: 'auto', display: 'block' }} />
            <Typography variant="h6" component="div">
              RAG Management Tool
            </Typography>
          </Box>

          {loading ? (
            <CircularProgress color="inherit" size={24} />
          ) : userInfo ? (
            <Box>
              <Button
                color="inherit"
                onClick={handleClick}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: 'secondary.main',
                    mr: 1,
                    fontSize: '0.875rem',
                  }}
                >
                  {userInfo.email.charAt(0).toUpperCase()}
                </Avatar>
                <Typography
                  variant="body2"
                  sx={{ display: { xs: 'none', sm: 'block' } }}
                >
                  {userInfo.email}
                </Typography>
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    minWidth: 200,
                    mt: 1,
                  },
                }}
              >
                {/* ユーザー情報 */}
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    ログイン中
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" noWrap>
                    {userInfo.email}
                  </Typography>
                </Box>

                <Divider />

                {/* パスワード変更 */}
                <MenuItem onClick={() => { handleClose(); setChangePasswordOpen(true); }}>
                  <ListItemIcon>
                    <LockResetIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>パスワード変更</ListItemText>
                </MenuItem>

                {/* 管理者メニュー */}
                {isAdmin && (onNavigateToUserManagement || onNavigateToTop) && <Divider />}

                {isAdmin && onNavigateToUserManagement && (
                  <MenuItem onClick={handleUserManagement}>
                    <ListItemIcon>
                      <PeopleIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>ユーザー管理</ListItemText>
                  </MenuItem>
                )}

                {isAdmin && !onNavigateToUserManagement && onNavigateToTop && (
                  <MenuItem onClick={() => { handleClose(); onNavigateToTop(); }}>
                    <ListItemIcon>
                      <CloudUploadIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>ファイル管理</ListItemText>
                  </MenuItem>
                )}

                <Divider />

                {/* ログアウト */}
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>ログアウト</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          ) : null}
        </Toolbar>
      </AppBar>

      {/* メインコンテンツ */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'grey.100',
          p: 3,
        }}
      >
        {children}
      </Box>

      {/* パスワード変更ダイアログ */}
      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />

      {/* フッター */}
      <Box
        component="footer"
        sx={{
          py: 2,
          px: 3,
          bgcolor: 'grey.200',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          RAG Management Tool v{appConfig.version}
        </Typography>
      </Box>
    </Box>
  );
};

export default AppLayout;
